import type { RegistrationData } from '@/types/registration';
import { wregEligStep, wregEligSwitch } from '@/components/webreg/wregEligibility';
import { wregAlignNeedsAttestation } from '@/components/webreg/wregAlignment';
import { wregHasShipped } from '@/components/webreg/wregFlowState';
import type { WregStep } from '@/components/webreg/wregSteps';

export type WregGateCtx = {
  data: RegistrationData;
  devEnabled: boolean;
};

export type WregGate = (ctx: WregGateCtx) => boolean;

const reachedScreener = (data: RegistrationData): boolean => {
  const code = data.wreg.eligCode;
  return code !== null && wregEligStep(code) === 'screener';
};

const needsSwitch = (data: RegistrationData): boolean =>
  (data.wreg.eligCode !== null && wregEligSwitch(data.wreg.eligCode)) ||
  wregAlignNeedsAttestation(data.wreg.alignCode ?? undefined);

const exitIs = (step: WregStep): WregGate => ({ data }) => data.wreg.exit === step;

// The MBI is no longer persisted (RND-38783), so every screen between step 1 and the alignment
// call has to check it: a refresh drops it, and a gate that passes without it walks the member to
// the address CTA where alignment posts an empty MBI and CMS answers 422 on the last click of the
// flow. Bouncing to the resume step instead sends them back to /details with one field to re-type.
const hasMbi = (data: RegistrationData): boolean => data.medicareCoverage.mbi !== '';

export const WREG_GATE: Record<WregStep, WregGate> = {
  intake: () => true,
  screener: ({ data }) => hasMbi(data) && reachedScreener(data),
  tracks: ({ devEnabled }) => devEnabled,
  agreement: ({ devEnabled }) => devEnabled,
  switch: ({ data }) => hasMbi(data) && data.selectedTracks.length > 0 && needsSwitch(data),
  // Gated on the screener's write, not on consentAgreed. Consent moved to the step-1 checkbox in
  // RND-38229, and a gate that reads a write from two steps earlier bounces anyone who refreshes
  // mid-flow before that write lands.
  labs: ({ data }) => hasMbi(data) && data.selectedTracks.length > 0,
  monitor: ({ data }) => hasMbi(data) && data.labsCompleted === true,
  allset: ({ data }) =>
    wregHasShipped(data) || data.wreg.exit === 'x-outreach' || data.wreg.exit === 'x-labspending',
  'x-notelig': exitIs('x-notelig'),
  'x-already': exitIs('x-already'),
  'x-control': exitIs('x-control'),
  'x-mismatch': exitIs('x-mismatch'),
  'x-noqualify': exitIs('x-noqualify'),
  'x-error': exitIs('x-error'),
  'x-switchok': exitIs('x-switchok'),
  'x-outreach': exitIs('x-outreach'),
  'x-labspending': exitIs('x-labspending'),
  'x-declined': exitIs('x-declined'),
};

export const wregStepIsAccessible = (step: WregStep, ctx: WregGateCtx): boolean =>
  WREG_GATE[step](ctx);
